import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactResizeDetector from 'react-resize-detector';
import { Box, Typography, Link } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import ActionsComments from '../../../../store/ducks/comments';

import SectionCard from '../../../../components/SectionCard';
import CommentGroup from './CommentGroup';
import InputComment from './InputComment';
import Loading from './Loading';
import useStyles from './styles';
import {
  convertObjToArr,
  paginateArr,
  sortArrByKey,
  usePrevious,
} from '../../../../utils';

const Comments = props => {
  const { onSectionChange } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { current, list } = useSelector(state => state.course);
  const [commentsRequested, setCommentsRequested] = useState(false);
  const [allowPostComment, setAllowPostComment] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState(true);
  const prevCurrent = usePrevious(current);

  const [commentsArr, setCommentsArr] = useState([]);
  const [topLevelComments, setTopLevelComments] = useState([]);
  const [, setCommentStoring] = useState(false);
  const [pagination, setPagination] = useState({
    offset: 3,
    visibleAmmount: 3,
    ammount: 0,
  });

  const checkLessonChange = useCallback(
    (course, lesson) =>
      prevCurrent &&
      (course !== prevCurrent.course || lesson !== prevCurrent.lesson),
    [prevCurrent],
  );

  useEffect(() => {
    if (current) {
      const { course, lesson } = current;
      if (prevCurrent && checkLessonChange(course, lesson)) {
        setCommentsRequested(false);
      }
    }
  }, [checkLessonChange, current, prevCurrent]);

  useEffect(() => {
    if (!loading) {
      const lessonChange = checkLessonChange(current?.course, current?.lesson);
      if (lessonChange) setLoading(true);
    }
  }, [current, loading, checkLessonChange]);

  useEffect(() => {
    if (!current || !list[current.course]?.courseDetailed) return;
    const hasCourse = current?.course in list;
    const hasLesson = current?.lesson in list[current.course]?.lessons;
    if (hasCourse && hasLesson) {
      const { course, lesson } = current;
      const courseInfos = list[course];
      const lessonInfos = courseInfos.lessons[lesson];
      onSectionChange('render', courseInfos.canComment);
      setShowComments(courseInfos.canComment);
      setAllowPostComment(!courseInfos.daysToComment);

      if (!lessonInfos.commentsLoaded) {
        if (!commentsRequested) {
          dispatch(ActionsComments.requestLessonComments(course, lesson));
          setCommentsRequested(true);
        }
      } else {
        const lessonComments = lessonInfos.comments;
        setComments(lessonComments);
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, list, commentsRequested, dispatch]);

  useEffect(() => {
    setCommentStoring(oldCommentStoring => {
      if (oldCommentStoring) {
        enqueueSnackbar('Comentário adicionado com sucesso!', {
          variant: 'success',
        });
        return false;
      }
      return oldCommentStoring;
    });
  }, [comments, enqueueSnackbar]);

  const handleTopLevelCommentsArr = useCallback((topLevelArr, pageSize) => {
    const commentsPagined = paginateArr(
      sortArrByKey(topLevelArr, 'createdAt', true),
      pageSize,
      1,
    );
    setTopLevelComments(sortArrByKey(commentsPagined, 'createdAt', false));
  }, []);

  const handlePagination = useCallback(action => {
    switch (action) {
      case 'showMore':
        setPagination(oldPagination => ({
          ...oldPagination,
          visibleAmmount: oldPagination.visibleAmmount + oldPagination.offset,
        }));
        break;
      case 'showLess':
        setPagination(oldPagination => ({
          ...oldPagination,
          visibleAmmount: oldPagination.offset,
        }));
        break;
      default:
        break;
    }
  }, []);

  const handleStoreComment = () => {
    setCommentStoring(true);
  };

  useEffect(() => {
    if (comments) {
      setCommentsArr(convertObjToArr(comments));
    }
  }, [comments, current]);

  useEffect(() => {
    const topLevelArr = commentsArr.filter(comment => !comment.parentId);
    setPagination(oldPagination => ({
      ...oldPagination,
      ammount: topLevelArr.length,
    }));
    handleTopLevelCommentsArr(topLevelArr, pagination.visibleAmmount);
  }, [commentsArr, handleTopLevelCommentsArr, pagination.visibleAmmount]);

  useEffect(() => {
    const topLevelArr = commentsArr.filter(comment => !comment.parentId);
    handleTopLevelCommentsArr(topLevelArr, pagination.visibleAmmount);
  }, [commentsArr, handleTopLevelCommentsArr, pagination]);

  return loading ? (
    <SectionCard className="container-section">
      <Loading />
    </SectionCard>
  ) : (
    <>
      {showComments && (
        <SectionCard
          className="container-section"
          id="comments"
          title="Comentários"
        >
          <ReactResizeDetector
            handleHeight
            onResize={(width, height) => onSectionChange('height', height)}
          />
          <Box className={classes.container}>
            <Typography
              className="comments-ammount"
              variant="caption"
              component="i"
            >
              {pagination.ammount
                ? `Mostrando ${topLevelComments.length} de ${pagination.ammount} comentários.`
                : `Esta aula ainda não possuí comentários. ${
                    allowPostComment ? 'Seja o primeiro a comentar!' : ''
                  }`}
            </Typography>
            {pagination.ammount > pagination.offset && (
              <Link
                className="comments-show-more"
                underline="always"
                component="button"
                onClick={
                  () =>
                    handlePagination(
                      pagination.ammount > pagination.visibleAmmount
                        ? 'showMore'
                        : 'showLess',
                    )
                  // eslint-disable-next-line react/jsx-curly-newline
                }
              >
                {topLevelComments.length < pagination.ammount
                  ? 'Mostrar comentários mais antigos'
                  : 'Ocultar comentários antigos'}
              </Link>
            )}
            {!!topLevelComments.length &&
              topLevelComments.map(comment => (
                <CommentGroup
                  key={comment.id}
                  {...comment}
                  allowReply={allowPostComment}
                />
              ))}
            {allowPostComment && (
              <InputComment
                comments={topLevelComments}
                onStore={handleStoreComment}
              />
            )}
          </Box>
        </SectionCard>
      )}
    </>
  );
};

export default Comments;
